import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { clientsAPI, clientInvitationsAPI, teamAPI } from '../lib/api';
import { formatDate, getStatusColor } from '../lib/utils';
import { 
  Users, 
  Plus, 
  Search, 
  Mail, 
  Phone, 
  Building,
  MoreVertical,
  Edit,
  Trash2,
  ChevronRight,
  Send,
  CheckSquare,
  Square,
  UserCheck,
  Clock,
  Ban,
  RefreshCw,
  User,
  Briefcase,
  Power,
  PowerOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { PageLoader } from '../components/common/LoadingSpinner';
import { AddressAutocomplete } from '../components/common/AddressAutocomplete';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export const Clients = () => {
  const [clients, setClients] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [bulkInviteDialog, setBulkInviteDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClients, setSelectedClients] = useState([]);
  const [bulkInviteMessage, setBulkInviteMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('clients');
  const [deleteClientId, setDeleteClientId] = useState(null);
  const [deleteClientName, setDeleteClientName] = useState('');
  
  const CLIENT_TYPES = [
    { value: 'individual', label: 'Individual' },
    { value: 'corporation', label: 'Corporation' },
    { value: 'non_profit', label: 'Non-profit' },
    { value: 'government', label: 'Government' },
  ];
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    client_type: 'individual',
    representatives: [],
    assigned_to: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, invitationsRes, teamRes] = await Promise.all([
        clientsAPI.getAll(),
        clientInvitationsAPI.getAll().catch(() => ({ data: [] })),
        teamAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setClients(clientsRes.data);
      setInvitations(invitationsRes.data || []);
      setTeamMembers(teamRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await clientsAPI.update(editingClient.client_id, formData);
        toast.success('Client updated successfully');
      } else {
        await clientsAPI.create(formData);
        toast.success('Client created successfully');
      }
      fetchClients();
      resetForm();
    } catch (error) {
      console.error('Failed to save client:', error);
      toast.error('Failed to save client');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      first_name: client.first_name || client.name?.split(' ')[0] || '',
      last_name: client.last_name || client.name?.split(' ').slice(1).join(' ') || '',
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      address: client.address || '',
      client_type: client.client_type || 'individual',
      representatives: client.representatives || [],
      assigned_to: client.assigned_to || '',
      notes: client.notes || '',
    });
    // Force dialog to open
    setTimeout(() => setIsDialogOpen(true), 0);
  };

  const handleDelete = async () => {
    if (!deleteClientId) return;
    try {
      await clientsAPI.delete(deleteClientId);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      console.error('Failed to delete client:', error);
      toast.error('Failed to delete client');
    } finally {
      setDeleteClientId(null);
      setDeleteClientName('');
    }
  };

  const handleToggleStatus = async (client) => {
    const newStatus = client.status === 'active' ? 'inactive' : 'active';
    try {
      await clientsAPI.update(client.client_id, { status: newStatus });
      toast.success(`Client ${newStatus === 'active' ? 'enabled' : 'disabled'}`);
      fetchClients();
    } catch (error) {
      console.error('Failed to update client status:', error);
      toast.error('Failed to update client status');
    }
  };

  const resetForm = () => {
    setFormData({ 
      first_name: '', 
      last_name: '', 
      email: '', 
      phone: '', 
      company: '', 
      address: '', 
      client_type: 'individual',
      representatives: [],
      assigned_to: '',
      notes: '' 
    });
    setEditingClient(null);
    setIsDialogOpen(false);
  };
  
  const addRepresentative = () => {
    setFormData(prev => ({
      ...prev,
      representatives: [...prev.representatives, { name: '', title: '', email: '', phone: '' }]
    }));
  };
  
  const updateRepresentative = (index, field, value) => {
    setFormData(prev => {
      const newReps = [...prev.representatives];
      newReps[index] = { ...newReps[index], [field]: value };
      return { ...prev, representatives: newReps };
    });
  };
  
  const removeRepresentative = (index) => {
    setFormData(prev => ({
      ...prev,
      representatives: prev.representatives.filter((_, i) => i !== index)
    }));
  };
  
  const showRepresentatives = ['corporation', 'non_profit', 'government'].includes(formData.client_type);

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const selectAllEligible = () => {
    const eligible = clients.filter(c => c.email && c.portal_status !== 'active');
    setSelectedClients(eligible.map(c => c.client_id));
  };

  const clearSelection = () => {
    setSelectedClients([]);
  };

  const handleBulkInvite = async () => {
    if (selectedClients.length === 0) return;
    setSubmitting(true);
    try {
      const response = await clientInvitationsAPI.bulkInvite(selectedClients, bulkInviteMessage);
      const results = response.data.results;
      
      if (results.sent.length > 0) {
        toast.success(`Sent ${results.sent.length} invitation(s)`);
      }
      if (results.skipped.length > 0) {
        toast.info(`Skipped ${results.skipped.length} client(s) (already invited or active)`);
      }
      if (results.failed.length > 0) {
        toast.error(`Failed to invite ${results.failed.length} client(s)`);
      }
      
      setBulkInviteDialog(false);
      setSelectedClients([]);
      setBulkInviteMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendInvitation = async (inviteId) => {
    try {
      const response = await clientInvitationsAPI.resend(inviteId);
      toast.success('Invitation resent');
      toast.info(`Setup URL: ${response.data.setup_url}`, { duration: 10000 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend');
    }
  };

  const handleCancelInvitation = async (inviteId, clientName) => {
    if (!window.confirm(`Cancel invitation for ${clientName}?`)) return;
    try {
      await clientInvitationsAPI.cancel(inviteId);
      toast.success('Invitation cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const isExpired = (expiresAt) => new Date(expiresAt) < new Date();

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingInvitations = invitations.filter(i => i.status === 'pending');
  const eligibleForInvite = clients.filter(c => c.email && c.portal_status !== 'active');

  if (loading) {
    return <PageLoader text="Loading clients..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Clients</h1>
          <p className="text-slate-500 mt-1">Manage your client relationships and portal access</p>
        </div>
        <div className="flex gap-2">
          {selectedClients.length > 0 && (
            <Button 
              variant="outline" 
              className="gap-2"
              onClick={() => setBulkInviteDialog(true)}
              data-testid="bulk-invite-btn"
            >
              <Send className="h-4 w-4" />
              Invite {selectedClients.length} to Portal
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-client-btn">
                <Plus className="h-4 w-4" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
                <DialogDescription>
                  {editingClient ? 'Update client information below.' : 'Enter the client details to create a new client record.'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* First Name & Last Name */}
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      required
                      data-testid="client-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      required
                      data-testid="client-last-name-input"
                    />
                  </div>
                  
                  {/* Company */}
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="company">Company (Optional)</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Company or organization name"
                      data-testid="client-company-input"
                    />
                  </div>
                  
                  {/* Email & Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="client-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      placeholder="(555) 123-4567"
                      data-testid="client-phone-input"
                    />
                  </div>
                  
                  {/* Address */}
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <AddressAutocomplete
                      id="address"
                      value={formData.address}
                      onChange={(value) => setFormData({ ...formData, address: value })}
                      placeholder="Start typing an address..."
                      data-testid="client-address-input"
                    />
                  </div>
                  
                  {/* Client Type */}
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="client_type">Client Type</Label>
                    <Select
                      value={formData.client_type}
                      onValueChange={(value) => setFormData({ ...formData, client_type: value, representatives: value === 'individual' ? [] : formData.representatives })}
                    >
                      <SelectTrigger data-testid="client-type-select">
                        <SelectValue placeholder="Select client type" />
                      </SelectTrigger>
                      <SelectContent>
                        {CLIENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              {type.value === 'individual' && <User className="h-4 w-4" />}
                              {type.value === 'corporation' && <Building className="h-4 w-4" />}
                              {type.value === 'non_profit' && <Users className="h-4 w-4" />}
                              {type.value === 'government' && <Briefcase className="h-4 w-4" />}
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Representatives Section - shows for Corporation, Non-profit, Government */}
                  {showRepresentatives && (
                    <div className="col-span-2 space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Representatives</Label>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={addRepresentative}
                          data-testid="add-representative-btn"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Representative
                        </Button>
                      </div>
                      
                      {formData.representatives.length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-4 border rounded-lg border-dashed">
                          No representatives added. Click &quot;Add Representative&quot; to add contact persons for this {formData.client_type === 'corporation' ? 'corporation' : formData.client_type === 'non_profit' ? 'non-profit' : 'government entity'}.
                        </p>
                      )}
                      
                      {formData.representatives.map((rep, index) => (
                        <div key={index} className="p-3 border rounded-lg bg-slate-50 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-700">Representative {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeRepresentative(index)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Name *</Label>
                              <Input
                                value={rep.name}
                                onChange={(e) => updateRepresentative(index, 'name', e.target.value)}
                                placeholder="Full name"
                                required
                                className="h-8 text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Title</Label>
                              <Input
                                value={rep.title || ''}
                                onChange={(e) => updateRepresentative(index, 'title', e.target.value)}
                                placeholder="e.g., CEO, Director"
                                className="h-8 text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Email</Label>
                              <Input
                                type="email"
                                value={rep.email || ''}
                                onChange={(e) => updateRepresentative(index, 'email', e.target.value)}
                                placeholder="email@example.com"
                                className="h-8 text-sm"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Phone</Label>
                              <Input
                                value={rep.phone || ''}
                                onChange={(e) => updateRepresentative(index, 'phone', e.target.value)}
                                placeholder="(555) 123-4567"
                                className="h-8 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Assign To */}
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="assigned_to">Assign To</Label>
                    <Select
                      value={formData.assigned_to || "unassigned"}
                      onValueChange={(value) => setFormData({ ...formData, assigned_to: value === "unassigned" ? "" : value })}
                    >
                      <SelectTrigger data-testid="client-assigned-to-select">
                        <SelectValue placeholder="Select team member" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {teamMembers.map((member) => (
                          <SelectItem key={member.user_id} value={member.user_id}>
                            {member.name} ({member.role})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Notes */}
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      placeholder="Additional notes about this client..."
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-client-btn">
                    {editingClient ? 'Update' : 'Create'} Client
                  </Button>
                </div>
              </form>
            </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="clients" className="gap-2">
            <Users className="h-4 w-4" />
            Clients ({clients.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" className="gap-2">
            <Mail className="h-4 w-4" />
            Portal Invitations
            {pendingInvitations.length > 0 && (
              <Badge className="ml-1 bg-blue-100 text-blue-800">{pendingInvitations.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Clients Tab */}
        <TabsContent value="clients" className="mt-6">
          {/* Search and Select All */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-clients-input"
              />
            </div>
            {eligibleForInvite.length > 0 && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={selectedClients.length === eligibleForInvite.length ? clearSelection : selectAllEligible}
                  className="gap-2"
                >
                  {selectedClients.length === eligibleForInvite.length ? (
                    <><Square className="h-4 w-4" /> Deselect All</>
                  ) : (
                    <><CheckSquare className="h-4 w-4" /> Select All Eligible ({eligibleForInvite.length})</>
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Clients Grid */}
          {filteredClients.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="clients-grid">
              {filteredClients.map((client) => {
                const canInvite = client.email && client.portal_status !== 'active';
                const isSelected = selectedClients.includes(client.client_id);
                
                return (
                  <Card 
                    key={client.client_id} 
                    className={`hover-lift transition-all ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50/30' : ''}`}
                    data-testid={`client-card-${client.client_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          {canInvite && (
                            <Checkbox 
                              checked={isSelected}
                              onCheckedChange={() => toggleClientSelection(client.client_id)}
                              className="mt-1"
                              data-testid={`select-client-${client.client_id}`}
                            />
                          )}
                          <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <Link 
                              to={`/client/${client.client_id}`}
                              className="font-semibold text-slate-900 hover:text-blue-600 hover:underline transition-colors"
                              data-testid={`client-name-link-${client.client_id}`}
                            >
                              {client.name}
                            </Link>
                            {client.company && (
                              <p className="text-sm text-slate-500">{client.company}</p>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(client)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(client)}>
                              {client.status === 'active' ? (
                                <>
                                  <PowerOff className="h-4 w-4 mr-2" />
                                  Disable Client
                                </>
                              ) : (
                                <>
                                  <Power className="h-4 w-4 mr-2" />
                                  Enable Client
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => { setDeleteClientId(client.client_id); setDeleteClientName(client.name); }}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="mt-4 space-y-2">
                        {client.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Mail className="h-4 w-4 text-slate-400" />
                            <span className="truncate">{client.email}</span>
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="h-4 w-4 text-slate-400" />
                            <span>{client.phone}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(client.status)}>
                            {client.status}
                          </Badge>
                          {client.portal_status === 'active' && (
                            <Badge className="bg-green-100 text-green-800">
                              <UserCheck className="h-3 w-3 mr-1" />
                              Portal
                            </Badge>
                          )}
                          {client.portal_status === 'invited' && (
                            <Badge className="bg-blue-100 text-blue-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Invited
                            </Badge>
                          )}
                        </div>
                        <Link 
                          to={`/client/${client.client_id}`}
                          className="flex items-center gap-1 text-xs text-slate-400 hover:text-blue-600 transition-colors"
                          data-testid={`view-client-link-${client.client_id}`}
                        >
                          View <ChevronRight className="h-3 w-3" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No clients found</h3>
                <p className="text-slate-500 mb-4">
                  {searchTerm ? 'Try a different search term' : 'Get started by adding your first client'}
                </p>
                {!searchTerm && (
                  <Button onClick={() => setIsDialogOpen(true)} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Add Client
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Portal Invitations Tab */}
        <TabsContent value="invitations" className="mt-6">
          <Card data-testid="client-invitations-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Client Portal Invitations
              </CardTitle>
              <CardDescription>Manage pending and past portal invitations</CardDescription>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p>No portal invitations sent yet</p>
                  <p className="text-sm mt-1">Select clients and click &quot;Invite to Portal&quot; to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invitations.map((inv) => {
                    const expired = isExpired(inv.expires_at);
                    const isPending = inv.status === 'pending';
                    
                    return (
                      <div 
                        key={inv.invite_id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          inv.status === 'accepted' ? 'bg-green-50 border-green-200' :
                          expired ? 'bg-red-50 border-red-200' :
                          'bg-slate-50 border-slate-200'
                        }`}
                        data-testid={`client-invitation-${inv.invite_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                            inv.status === 'accepted' ? 'bg-green-100' :
                            expired ? 'bg-red-100' : 'bg-blue-100'
                          }`}>
                            {inv.status === 'accepted' ? (
                              <UserCheck className="h-5 w-5 text-green-600" />
                            ) : expired ? (
                              <Clock className="h-5 w-5 text-red-600" />
                            ) : (
                              <Mail className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{inv.client_name}</h4>
                            <p className="text-sm text-slate-500">{inv.email}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              Invited {formatDate(inv.created_at)} by {inv.invited_by_name}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={
                            inv.status === 'accepted' ? 'border-green-300 text-green-700' :
                            expired ? 'border-red-300 text-red-700' :
                            'border-blue-300 text-blue-700'
                          }>
                            {inv.status === 'accepted' ? 'Accepted' : expired ? 'Expired' : 'Pending'}
                          </Badge>
                          {isPending && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleResendInvitation(inv.invite_id)}>
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Resend Invitation
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleCancelInvitation(inv.invite_id, inv.client_name)}
                                  className="text-red-600"
                                >
                                  <Ban className="h-4 w-4 mr-2" />
                                  Cancel Invitation
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bulk Invite Dialog */}
      <Dialog open={bulkInviteDialog} onOpenChange={setBulkInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Clients to Portal</DialogTitle>
            <DialogDescription>
              Send portal invitations to {selectedClients.length} selected client(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Selected clients will receive an email invitation to set up their client portal account.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bulkMessage">Custom Message (optional)</Label>
              <Textarea
                id="bulkMessage"
                value={bulkInviteMessage}
                onChange={(e) => setBulkInviteMessage(e.target.value)}
                placeholder="Add a personal message to the invitation..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setBulkInviteDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkInvite} 
                disabled={submitting}
                className="gap-2"
                data-testid="confirm-bulk-invite-btn"
              >
                {submitting ? 'Sending...' : (
                  <>
                    <Send className="h-4 w-4" />
                    Send Invitations
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteClientId} onOpenChange={() => { setDeleteClientId(null); setDeleteClientName(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteClientName}</strong>? This action cannot be undone. 
              All associated data including cases, documents, and billing records may be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete Client
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Clients;
